/**
 * Bloqueador central de navegação (SPEC 4.14: "mudanças não salvas" em Ajustes, e qualquer
 * formulário longo no futuro). Substitui o contorno que embrulhava `history.pushState`.
 *
 * Como funciona:
 * - O `<Router aroundNav={guardNavigation}>` (app/App.tsx) passa TODA navegação do wouter por aqui:
 *   `<Link>`, `navigate()` do shell, da paleta ⌘K, dos atalhos g+letra, do banner P0 e `<Redirect>`.
 * - Só bloqueia quando o CAMINHO muda; trocar `?p=` ou abrir um sheet por query não pede nada.
 * - O Voltar do navegador é cancelado pela Navigation API antes de a URL mudar; quando ela não
 *   deixa cancelar (sem ativação do usuário) ou não existe, o `popstate` em captura devolve a
 *   pessoa à tela e pede confirmação (destino `NAV_BACK`). Voltar só de seção (mesmo caminho,
 *   outra query) não pergunta nada.
 * - Fechar a aba com mudanças pede a confirmação nativa (`beforeunload`).
 *
 * Uso na tela:
 *   const { proceed } = useNavigationBlocker(dirty, (to) => setPending(to));
 *   // no diálogo: [Descartar] → proceed(pending)  ·  [Continuar editando] → setPending(null)
 */
import { useCallback, useEffect, useRef } from 'react';
import type { AroundNavHandler } from 'wouter';
import { useLocation } from 'wouter';

/** Destino especial: a pessoa apertou Voltar no navegador. */
export const NAV_BACK = '__voltar__';

interface Blocker {
  onBlocked(to: string): void;
}

const blockers = new Set<Blocker>();
/** Passe livre de uma navegação (o "Descartar" do diálogo). */
let bypass = false;

/** Caminho de destino quando ele sai do caminho atual; `null` = mesma tela (não bloqueia). */
export function leavesPath(to: string | URL | null | undefined, current: Location = location): string | null {
  if (to == null) return null;
  const next = new URL(String(to), current.href);
  if (next.origin !== current.origin || next.pathname === current.pathname) return null;
  return next.pathname + next.search;
}

/** Há alguma tela com mudanças não salvas? */
export function isNavigationBlocked(): boolean {
  return blockers.size > 0;
}

/** Decide se a navegação para `to` segue; avisa a tela que bloqueou. */
function intercept(to: string): boolean {
  if (bypass) {
    bypass = false;
    return false;
  }
  if (blockers.size === 0 || typeof location === 'undefined') return false;
  const next = leavesPath(to);
  if (!next) return false;
  // A tela mais recente decide (só existe uma por vez na prática).
  const last = [...blockers].pop();
  last?.onBlocked(next);
  return true;
}

/** `aroundNav` do wouter: toda navegação do app passa por aqui. */
export const guardNavigation: AroundNavHandler = (navigate, to, options) => {
  if (intercept(String(to))) return;
  navigate(to, options);
};

/** O mínimo da Navigation API usado aqui (fora do lib.dom de algumas versões do TS). */
interface NavigateEventLike {
  navigationType: 'push' | 'replace' | 'reload' | 'traverse';
  cancelable: boolean;
  destination?: { url: string };
  preventDefault(): void;
}
interface NavigationLike {
  addEventListener(type: 'navigate', fn: (e: NavigateEventLike) => void): void;
  removeEventListener(type: 'navigate', fn: (e: NavigateEventLike) => void): void;
}

/**
 * Registra a tela enquanto `dirty`. `onBlocked(to)` recebe o destino (ou `NAV_BACK`); a tela
 * mostra a confirmação e chama `proceed(to)` para seguir.
 */
export function useNavigationBlocker(
  dirty: boolean,
  onBlocked: (to: string) => void,
): { proceed(to: string): void } {
  const [, navigate] = useLocation();
  const cb = useRef(onBlocked);
  cb.current = onBlocked;
  const passing = useRef(false);

  useEffect(() => {
    if (!dirty) return;
    passing.current = false;
    const blocker: Blocker = { onBlocked: (to) => cb.current(to) };
    blockers.add(blocker);
    // Onde a pessoa está (caminho + query). Trocar só a query (seção, aba) não sai da tela.
    let here = location.pathname + location.search;
    const herePath = () => new URL(here, location.href).pathname;

    const nav = (window as Window & { navigation?: NavigationLike }).navigation;
    const onNavigate = (e: NavigateEventLike) => {
      if (passing.current) return;
      const dest = e.destination?.url;
      if (e.navigationType !== 'traverse') {
        // Navegação dentro da mesma tela (outra seção): passa a ser o "aqui".
        if (dest && !leavesPath(dest)) {
          const u = new URL(dest, location.href);
          here = u.pathname + u.search;
        }
        return;
      }
      if (dest && !leavesPath(dest)) return;
      // Sem ativação do usuário o Chrome não deixa cancelar o Voltar: o popstate abaixo devolve.
      if (!e.cancelable) return;
      e.preventDefault();
      cb.current(NAV_BACK);
    };
    const onPop = () => {
      if (passing.current || location.pathname === herePath()) {
        here = location.pathname + location.search;
        return;
      }
      history.pushState(null, '', here);
      cb.current(NAV_BACK);
    };
    const onUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    if (nav) nav.addEventListener('navigate', onNavigate);
    window.addEventListener('popstate', onPop, true);
    window.addEventListener('beforeunload', onUnload);
    return () => {
      blockers.delete(blocker);
      nav?.removeEventListener('navigate', onNavigate);
      window.removeEventListener('popstate', onPop, true);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [dirty]);

  const proceed = useCallback(
    (to: string) => {
      passing.current = true;
      if (to === NAV_BACK) {
        history.back();
        return;
      }
      bypass = true;
      navigate(to);
      // Se a navegação não passou pelo aroundNav (mesmo caminho), o passe livre não fica pendurado.
      bypass = false;
    },
    [navigate],
  );
  return { proceed };
}
